import { api } from './generated-client'

// Backend response types (snake_case converted to camelCase by interceptor)
export interface ClientStateResponse {
  userId: number
  state: string
  stateDescription: string
  updatedAt: string
}

// Legacy interface for backward compatibility
export interface OnboardingStatus {
  currentStep: string
  completedSteps: string[]
  status: 'pending' | 'in_progress' | 'completed' | 'rejected'
}

export interface ContactInfo {
  formalName?: string
  taxNo?: string
  birthdate?: string
  personType?: string
  contacter?: string
  telephone?: string
  contactAddress?: string
  contactPostalCode?: string
  lineId?: string
  invoiceType?: string
  invoiceBusinessNo?: string
  invoiceBusinessTitle?: string
  electronicInvoiceCarrier?: string
  defaultContractAmount?: number
}

// Legacy interface for backward compatibility
export interface PersonalInfo {
  name: string
  idNumber: string
  birthday: string
  phone: string
  email: string
  address: string
}

export interface QuestionnaireAnswer {
  questionId: string
  answer: string | string[]
}

export interface Contract {
  id: number
  userId: number
  startDate: string
  endDate: string
  createdAt: string
  amount: number
}

/**
 * 簽約關卡（對齊後端 ClientState，hyphen-case 與 router path / join store step id 一致）。
 * `run` 不是一個關卡，而是「全部完成」終態。
 */
export const ONBOARDING_STEP_IDS = [
  'personal-info',
  'questionnaire',
  'contract',
  'account-opening',
  'acceptance-report',
  'phone-confirmation',
  'auditing',
] as const

export type OnboardingStepId = (typeof ONBOARDING_STEP_IDS)[number]

/**
 * 後端 ClientState 字串 → 前端關卡 id。
 * Re* 分支對映回對應主關卡（重填問卷→questionnaire、重簽合約→contract）。
 * `run` 不對映任何關卡（代表全數完成）。
 */
const STATE_TO_STEP: Record<string, OnboardingStepId> = {
  fill_personal_info: 'personal-info',
  fill_questionnaire: 'questionnaire',
  re_fill_questionnaire: 'questionnaire',
  create_contract: 'contract',
  re_create_contract: 'contract',
  propose_account_opening: 'account-opening',
  sign_acceptance_report: 'acceptance-report',
  wait_phone_confirmation: 'phone-confirmation',
  auditing: 'auditing',
}

// Helper to convert ClientStateResponse to OnboardingStatus for legacy components
function toOnboardingStatus(response: ClientStateResponse): OnboardingStatus {
  const state = response.state

  // run = 全部完成；locked = 審核未通過
  if (state === 'run') {
    return {
      currentStep: 'auditing',
      completedSteps: [...ONBOARDING_STEP_IDS],
      status: 'completed',
    }
  }
  if (state === 'locked') {
    return {
      currentStep: 'personal-info',
      completedSteps: [],
      status: 'rejected',
    }
  }

  const currentStep = STATE_TO_STEP[state] ?? 'personal-info'
  const currentStepIndex = ONBOARDING_STEP_IDS.indexOf(currentStep as OnboardingStepId)
  const completedSteps = currentStepIndex > 0
    ? ONBOARDING_STEP_IDS.slice(0, currentStepIndex)
    : []

  // 第一關（填個資）視為 pending，其餘進行中
  const status: OnboardingStatus['status'] =
    currentStep === 'personal-info' ? 'pending' : 'in_progress'

  return {
    currentStep,
    completedSteps,
    status,
  }
}

// Helper to convert ContactInfo to PersonalInfo for legacy components
function toPersonalInfo(contact: ContactInfo): PersonalInfo {
  return {
    name: contact.formalName || '',
    idNumber: contact.taxNo || '',
    birthday: contact.birthdate || '',
    phone: contact.telephone || '',
    email: '', // Not in contact_info, would need from user
    address: contact.contactAddress || '',
  }
}

export const onboardingApi = {
  /**
   * Get client onboarding state
   * Backend: GET /join/client_state
   */
  async getStatus(): Promise<OnboardingStatus> {
    const { data, error } = await api.GET('/join/client_state')
    if (error || !data) throw new Error('Failed to fetch client state')
    return toOnboardingStatus(data as ClientStateResponse)
  },

  /**
   * Get raw client state (for components that need the actual state)
   * Backend: GET /join/client_state
   */
  async getClientState(): Promise<ClientStateResponse> {
    const { data, error } = await api.GET('/join/client_state')
    if (error || !data) throw new Error('Failed to fetch client state')
    return data as ClientStateResponse
  },

  /**
   * Get contact info (personal info)
   * Backend: GET /join/contact_info
   */
  async getPersonalInfo(): Promise<PersonalInfo> {
    const { data, error } = await api.GET('/join/contact_info')
    if (error || !data) throw new Error('Failed to fetch contact info')
    return toPersonalInfo(data as ContactInfo)
  },

  /**
   * Get raw contact info
   * Backend: GET /join/contact_info
   */
  async getContactInfo(): Promise<ContactInfo> {
    const { data, error } = await api.GET('/join/contact_info')
    if (error || !data) throw new Error('Failed to fetch contact info')
    return data as ContactInfo
  },

  /**
   * Update contact info
   * Backend: POST /join/contact_info
   */
  async updatePersonalInfo(info: Partial<PersonalInfo>): Promise<void> {
    // Convert PersonalInfo fields to ContactInfo fields
    const contactInfo: Partial<ContactInfo> = {
      formalName: info.name,
      taxNo: info.idNumber,
      birthdate: info.birthday,
      telephone: info.phone,
      contactAddress: info.address,
    }
    const { error } = await api.POST('/join/contact_info', { body: contactInfo })
    if (error) throw new Error('Failed to update contact info')
  },

  /**
   * Update contact info (raw)
   * Backend: POST /join/contact_info
   */
  async saveContactInfo(info: Partial<ContactInfo>): Promise<void> {
    const { error } = await api.POST('/join/contact_info', { body: info })
    if (error) throw new Error('Failed to save contact info')
  },

  /**
   * Check questionnaire pass status (external SvCake-backed).
   * Backend: GET /join/questionnaire/check
   * 回傳 passed + message（後端誠實判定：questionnaire_passed_at / UAT 開關 / 未配置）。
   */
  async getQuestionnaire(): Promise<{ passed: boolean; message: string }> {
    const { data, error } = await api.GET('/join/questionnaire/check')
    if (error || !data) throw new Error('無法取得問卷狀態')
    return data as { passed: boolean; message: string }
  },

  /**
   * Confirm questionnaire completion ("我已完成").
   * Backend: POST /join/check_questionnaire — re-checks SvCake pass status and,
   * if passed, advances client state (fill_questionnaire → create_contract).
   * 回傳是否通過 + 訊息，讓前端誠實顯示尚未通過的原因。
   */
  async confirmQuestionnaire(): Promise<{ passed: boolean; message: string }> {
    const { data, error } = await api.POST('/join/check_questionnaire', {})
    if (error || !data) throw new Error('確認問卷完成失敗')
    return data as { passed: boolean; message: string }
  },

  /**
   * Get latest contract
   * Backend: GET /join/dev_contract/latest
   */
  async getContract(): Promise<Contract | null> {
    const { data, error } = await api.GET('/join/dev_contract/latest')
    if (error) {
      // If 404, return null (no contract yet)
      return null
    }
    if (!data) return null
    return data as unknown as Contract
  },

  /**
   * List all contracts
   * Backend: GET /join/dev_contract
   */
  async listContracts(): Promise<Contract[]> {
    const { data, error } = await api.GET('/join/dev_contract')
    if (error || !data) throw new Error('Failed to list contracts')
    return data as unknown as Contract[]
  },

  /**
   * Create a new contract
   * Backend: POST /join/dev_contract
   */
  async createContract(amount = 0): Promise<Contract> {
    const { data, error } = await api.POST('/join/dev_contract', { body: { amount } })
    if (error || !data) throw new Error('Failed to create contract')
    return data as unknown as Contract
  },

  /**
   * Sign a contract (party-A confirmation by the customer).
   * Backend: POST /join/dev_contract/:id/sign
   *
   * 在 PEACE，建立合約即已推進 client state；此端點僅於合約記錄寫入簽署戳記，
   * 讓合約進入「已簽署」狀態，使客戶得以走過合約這關。
   */
  async signContract(contractId: number, signatureBase64?: string): Promise<void> {
    const { error } = await api.POST('/join/dev_contract/{id}/sign', {
      params: { path: { id: contractId } },
      body: { signatureBase64 },
    })
    if (error) throw new Error('簽署合約失敗')
  },

  /**
   * Download contract PDF
   * Backend: GET /join/dev_contract/self/latest/download
   */
  async downloadContractPdf(): Promise<Blob> {
    const { data, error } = await api.GET('/join/dev_contract/self/latest/download', {
      parseAs: 'blob'
    })
    if (error || !data) throw new Error('Failed to download contract PDF')
    return data as Blob
  },

  /**
   * Download specific contract PDF
   * Backend: GET /join/dev_contract/:id/download
   */
  async downloadContractById(contractId: number): Promise<Blob> {
    const { data, error } = await api.GET('/join/dev_contract/{id}/download', {
      params: { path: { id: contractId } },
      parseAs: 'blob'
    })
    if (error || !data) throw new Error('Failed to download contract PDF')
    return data as Blob
  },
}
